import { useEffect, useState } from 'react';
import { useMaintenanceWindow, usePlatformFlags } from '@askporter/api-client';
import type { MaintenanceWindow } from '@askporter/api-client';
import { useTranslation } from '@askporter/config-provider';
import { MAINTENANCE_MODE_FLAG } from '../MaintenanceBanner/useMaintenanceCopy';

/**
 * How much notice to give before a window starts, in minutes, by window type.
 *
 * Configurable here rather than hard-coded at the call site, and keyed off
 * `windowType` because the ticket's own reason for wanting it configurable is
 * that routine and emergency windows warrant different notice (NS-2465).
 *
 * An emergency gets a longer lead because there is less of it to give: the
 * moment we learn one is coming is the moment worth saying so. A routine
 * window is known well in advance, so ten minutes is a prompt to finish up
 * rather than an alarm.
 *
 * These are the **fallback**. A window may carry its own `warningLeadMinutes`,
 * declared by the operator, and that wins where present — the values here only
 * apply to a window that did not ask for anything specific.
 *
 * NS-2444 measured propagation on dev at under a minute per toggle, so ten
 * minutes covers it comfortably. Staging and production were never measured;
 * if either turns out slower, a per-window lead time can correct it without a
 * deploy, which these constants cannot.
 */
export const WARNING_LEAD_MINUTES: Record<string, number> = {
  routine: 10,
  emergency: 30,
};

/** Used when the window carries no recognised type. */
export const DEFAULT_WARNING_LEAD_MINUTES = 10;

/**
 * How often the countdown is re-evaluated, independent of the network poll.
 *
 * The flags poll is a minute apart, so deriving the warning only when a
 * response lands would let it appear up to a minute late — turning ten minutes'
 * notice into nine. Re-checking locally keeps the boundary tight without
 * touching the network, and only runs while a window is actually declared.
 */
const TICK_MS = 15_000;

/**
 * The notice this window should get, in minutes.
 *
 * A lead time declared on the window wins: the operator knew something about
 * this particular window that no client default can express. Anything absent,
 * zero or negative falls through to the per-type default rather than
 * suppressing the warning — the platform bounds the value at declaration, so a
 * bad one here means something upstream went wrong, and silently showing
 * nothing is the worst available response to that.
 */
export const leadMinutesFor = (windowType?: string, declaredLeadMinutes?: number): number => {
  if (typeof declaredLeadMinutes === 'number' && Number.isFinite(declaredLeadMinutes) && declaredLeadMinutes > 0) {
    return declaredLeadMinutes;
  }

  return (windowType && WARNING_LEAD_MINUTES[windowType]) || DEFAULT_WARNING_LEAD_MINUTES;
};

/**
 * Whether a declared window is close enough to warn about, and when it starts.
 *
 * Returns `isWarning: false` in every case that is not a genuine run-up to a
 * window, which covers several of the ticket's unhappy paths at once:
 *
 * - **No window declared, or one that was cancelled.** The flags read simply
 *   omits `maintenanceWindow`, so a cancelled window stops warning on the next
 *   poll with no reload. A rescheduled one re-derives from the new time the
 *   same way.
 * - **No scheduled start.** An emergency window declared to take effect
 *   immediately has no meaningful start, so there is nothing to count down to
 *   and nothing useful to say.
 * - **The window has already begun** — either the flag has flipped or the start
 *   time has passed. Both are checked, because relying on the flag alone would
 *   leave "starting soon" sitting above "maintenance in progress" for however
 *   long the flag took to arrive.
 *
 * A window scheduled with less notice than the lead time still warns, rather
 * than falling through the gap: the test is that the start is within the lead,
 * not that it is exactly the lead away.
 */
export interface MaintenanceWarning {
  /** Whether a window is close enough to warrant warning the user. */
  isWarning: boolean;
  /** The declared window, when there is one. */
  window?: MaintenanceWindow;
  /** Scheduled start, formatted in the user's own locale and timezone. */
  startsAt: string;
  /** Whole minutes until the window starts, floored at zero. */
  minutesUntil: number;
}

export const useMaintenanceWarning = (): MaintenanceWarning => {
  const window_ = useMaintenanceWindow();
  const flags = usePlatformFlags();
  const { i18n } = useTranslation();

  const scheduledStart = window_?.scheduledStart;
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!scheduledStart) return undefined;

    const timer = globalThis.setInterval(() => setNow(Date.now()), TICK_MS);
    return () => globalThis.clearInterval(timer);
  }, [scheduledStart]);

  const startMs = scheduledStart ? Date.parse(scheduledStart) : Number.NaN;
  const isValidStart = !Number.isNaN(startMs);
  const msUntil = startMs - now;
  const leadMs = leadMinutesFor(window_?.windowType, window_?.warningLeadMinutes) * 60_000;

  const isWarning = isValidStart && msUntil > 0 && msUntil <= leadMs && !flags[MAINTENANCE_MODE_FLAG];

  // Intl rather than a date library: it resolves the viewer's own timezone with
  // no configuration, which is the acceptance criterion, and component-library
  // has no established date-formatting dependency to match.
  const startsAt = isValidStart
    ? new Intl.DateTimeFormat(i18n?.language || undefined, {
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short',
      }).format(new Date(startMs))
    : '';

  return {
    isWarning,
    window: window_,
    startsAt,
    minutesUntil: isValidStart ? Math.max(0, Math.ceil(msUntil / 60_000)) : 0,
  };
};
